import { useTrainComposition } from "features/CenterContent/VehicleDetails/TrainDetails/TrainComposition/useTrainComposition";
import { CommonTrainInfoFormProps } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/TrainInfoForm";
import { CommonSubTypeProps } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/TrainInfoFormModal";
import { AffectedStopsFields } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/subTypeInputs/AffectedStopsFields";
import {
  calcTrainCompMetaData,
  getDefaultCapacity,
  getNumPassengerCarsFromLineId,
} from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/subTypeInputs/utils/changedCapacity";
import { TrainContext } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/subTypeInputs/utils/formContextWrappers";
import { EitherTrainFormSchema } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/types/formSchema";
import { useTrainInfoContext } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/useTrainInfoContext";
import { FC, useEffect } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  Box,
  Field,
  HStack,
  NumericStepper,
  Text,
  VStack,
} from "shared/components/Spor";

const MIN_CARS_OLD = 1;
const MIN_CARS_NEW = 2;
const MAX_CARS = 15;

/**
 * Could be combined with TrainCapacityReduced.tsx to make TrainCapacityBase.tsx
 * However, this component is rarely touched so its likely not worth the time
 */
export const TrainCapacityIncreased: FC<
  CommonTrainInfoFormProps & CommonSubTypeProps & TrainContext
> = (props) => {
  const { t } = useTranslation();
  const { mode, trainId, trainFormPrefix } = props;

  const { getValues, setValue, control } =
    useFormContext<EitherTrainFormSchema>();

  // Set capacity unit once on first load
  useEffect(() => {
    setValue(`${trainFormPrefix}.oldCapacityUnit`, "CARS");
    setValue(`${trainFormPrefix}.newCapacityUnit`, "CARS");
  }, []);

  const oldCapacity =
    getValues(`${trainFormPrefix}.oldCapacity`) ?? MIN_CARS_OLD;
  const newCapacity =
    getValues(`${trainFormPrefix}.newCapacity`) ?? MIN_CARS_NEW;

  const { status: compositionStatus, data: compositionData } =
    useTrainComposition(trainId);

  const { useTrainRouteBatch } = useTrainInfoContext();
  const { data: trainRoute } = useTrainRouteBatch(trainId);

  // Autofill based on train composition
  useEffect(() => {
    if (
      mode === "create" ||
      (!getValues(`${trainFormPrefix}.oldCapacity`) &&
        !getValues(`${trainFormPrefix}.newCapacity`))
    ) {
      if (
        compositionStatus === "success" &&
        compositionData.trainComposition.length > 0
      ) {
        const { numPassengerCars, type } = calcTrainCompMetaData(
          compositionData.trainComposition,
        );
        const capacity = getDefaultCapacity(
          numPassengerCars,
          type,
          "increased",
        );
        setValue(`${trainFormPrefix}.oldCapacity`, capacity.old);
        setValue(`${trainFormPrefix}.newCapacity`, capacity.new);
      } else {
        setValue(`${trainFormPrefix}.oldCapacity`, MIN_CARS_OLD);
        setValue(`${trainFormPrefix}.newCapacity`, MIN_CARS_NEW);
      }
    } else {
      const numPassengerCars = trainRoute?.lineId
        ? getNumPassengerCarsFromLineId(trainRoute.lineId)
        : 5;
      const capacity = getDefaultCapacity(
        numPassengerCars,
        "MULTIPLE_UNIT",
        "increased",
      );
      if (!getValues(`${trainFormPrefix}.oldCapacity`)) {
        setValue(`${trainFormPrefix}.oldCapacity`, capacity.old);
      }
      if (!getValues(`${trainFormPrefix}.newCapacity`)) {
        setValue(`${trainFormPrefix}.newCapacity`, capacity.new);
      }
    }
  }, [compositionStatus, mode]);

  return (
    <VStack width="100%" alignItems="flex-start" gap="18px">
      <AffectedStopsFields {...props} />
      <VStack paddingTop="1rem">
        <Box>
          <Field marginBottom="0">{t("Gammel kapasitet")}</Field>
          <HStack gap="1rem" alignContent="center" display="flex">
            <Controller
              control={control}
              name={`${trainFormPrefix}.oldCapacity`}
              render={({ field }) => (
                <NumericStepper
                  name={`${trainFormPrefix}.oldCapacity`}
                  minValue={MIN_CARS_OLD}
                  maxValue={MAX_CARS - 1}
                  defaultValue={oldCapacity}
                  value={field.value}
                  onValueChange={(value) => {
                    field.onChange(value);

                    // Make sure 'newCapacity' is always larger than 'oldCapacity'
                    if (value >= newCapacity) {
                      setValue(
                        `${trainFormPrefix}.newCapacity`,
                        Math.min(value + 1, MAX_CARS),
                        {
                          shouldValidate: true,
                        },
                      );
                    }
                  }}
                />
              )}
            />
            <Text>{t("Vogner")}</Text>
          </HStack>
        </Box>
        <Box>
          <Field marginBottom="0">{t("Ny kapasitet")}</Field>
          <HStack gap="1rem">
            <Controller
              control={control}
              name={`${trainFormPrefix}.newCapacity`}
              render={({ field }) => (
                <NumericStepper
                  name={`${trainFormPrefix}.newCapacity`}
                  minValue={oldCapacity + 1}
                  maxValue={MAX_CARS}
                  value={field.value}
                  onValueChange={(value) => field.onChange(value)}
                />
              )}
            />
            <Text>{t("Vogner")}</Text>
          </HStack>
        </Box>
      </VStack>
    </VStack>
  );
};
